import React, { useEffect, useRef } from "react";

/**
 * Subtle, low-opacity floating-cube background for the hero section.
 * Purely decorative — respects prefers-reduced-motion, skips on small
 * screens, and lazy-loads three.js in its own chunk so it never blocks
 * or bloats the initial page load.
 */
export default function HeroBackground() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const isSmallScreen = window.innerWidth < 768;

    if (prefersReducedMotion || isSmallScreen) {
      return;
    }

    let cleanup = () => {};
    let cancelled = false;
    let started = false;

    const start = () => {
      if (started || cancelled) return;
      started = true;
      import("three").then((THREE) => {
        if (cancelled || !containerRef.current) return;
        cleanup = mountScene(THREE, containerRef.current);
      });
    };

    const events = ["pointerdown", "keydown", "scroll", "touchstart"];
    events.forEach((eventName) =>
      window.addEventListener(eventName, start, { once: true, passive: true })
    );
    const timer = window.setTimeout(start, 8000);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      events.forEach((eventName) =>
        window.removeEventListener(eventName, start)
      );
      cleanup();
    };
  }, []);

  return <div className="hero-background" ref={containerRef} aria-hidden="true" />;
}

function mountScene(THREE, container) {
    let width = container.clientWidth || window.innerWidth;
    let height = container.clientHeight || window.innerHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.z = 5;

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: false,
      powerPreference: "low-power",
    });
    renderer.setSize(width, height);
    renderer.setPixelRatio(1);
    container.appendChild(renderer.domElement);

    const group = new THREE.Group();
    const cubes = [];
    const cubeCount = 26;
    const geometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);

    for (let i = 0; i < cubeCount; i++) {
      const color = new THREE.Color().setHSL(
        0.55 + Math.random() * 0.1,
        0.8,
        0.5 + Math.random() * 0.2
      );
      const material = new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity: 0.55,
        shininess: 90,
      });

      const cube = new THREE.Mesh(geometry, material);
      cube.position.set(
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 10,
        (Math.random() - 0.5) * 5
      );
      cube.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, 0);
      cube.userData.velocity = {
        x: (Math.random() - 0.5) * 0.01,
        y: (Math.random() - 0.5) * 0.01,
        z: (Math.random() - 0.5) * 0.01,
      };

      group.add(cube);
      cubes.push(cube);
    }
    scene.add(group);

    const light = new THREE.DirectionalLight(0xffffff, 1);
    light.position.set(5, 5, 5).normalize();
    scene.add(light);
    scene.add(new THREE.AmbientLight(0x404040));

    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      group.rotation.y += 0.0012;
      group.rotation.x += 0.0006;

      cubes.forEach((cube) => {
        cube.position.x += cube.userData.velocity.x;
        cube.position.y += cube.userData.velocity.y;
        cube.position.z += cube.userData.velocity.z;

        if (Math.abs(cube.position.x) > 5) cube.userData.velocity.x *= -1;
        if (Math.abs(cube.position.y) > 5) cube.userData.velocity.y *= -1;
        if (Math.abs(cube.position.z) > 3) cube.userData.velocity.z *= -1;

        cube.rotation.x += 0.006;
        cube.rotation.y += 0.006;
      });

      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      width = container.clientWidth || window.innerWidth;
      height = container.clientHeight || window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      geometry.dispose();
      cubes.forEach((cube) => cube.material.dispose());
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
}
